import { CbhIcon, type IconType, mergeSxProps } from "@clipboard-health/ui-components";
import {
  Alert,
  type AlertColor,
  AlertTitle,
  Collapse,
  type SxProps,
  type Theme,
} from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { type ReactNode, useState } from "react";

export interface CollapsableAlertProps {
  title: string;
  children: ReactNode;
  severity?: AlertColor;
  sx?: SxProps<Theme>;
}

const ICON_TYPE_BY_SEVERITY: Record<AlertColor, IconType> = {
  info: "info",
  warning: "triangle-exclamation",
  error: "triangle-exclamation",
  success: "checkmark-filled",
};

function getIntentPalette(theme: Theme, severity: AlertColor) {
  switch (severity) {
    case "info":
      return theme.palette.intent?.neutral;
    case "warning":
      return theme.palette.intent?.warning;
    case "error":
      return theme.palette.intent?.error;
    case "success":
      return theme.palette.intent?.success;
  }
}

export function CollapsableAlert(props: CollapsableAlertProps) {
  const { title, children, severity = "info", sx } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Alert
      severity={severity}
      icon={
        <CbhIcon
          type={ICON_TYPE_BY_SEVERITY[severity]}
          size="small"
          color={(theme) => getIntentPalette(theme, severity)?.icon}
        />
      }
      action={
        <IconButton
          iconType={isExpanded ? "chevron-up" : "chevron-down"}
          variant="ghost"
          aria-label={isExpanded ? "Collapse details" : "Expand details"}
          onClick={() => setIsExpanded((previous) => !previous)}
        />
      }
      sx={mergeSxProps((theme) => {
        const intent = getIntentPalette(theme, severity);
        return {
          backgroundColor: intent?.background,
          color: intent?.text,
          border: `1px solid ${intent?.border ?? "transparent"}`,
          borderRadius: theme.borderRadius?.medium,
          alignItems: "flex-start",
          "& .MuiAlert-icon": { padding: 0, marginTop: 5 },
          "& .MuiAlert-action": { padding: 0 },
          paddingX: 4,
          paddingY: 0,
        };
      }, sx)}
    >
      <AlertTitle
        sx={(theme) => {
          const intent = getIntentPalette(theme, severity);
          return {
            fontWeight: "bold",
            color: intent?.text,
            marginTop: 2,
          };
        }}
      >
        {title}
      </AlertTitle>
      <Collapse in={isExpanded}>{children}</Collapse>
    </Alert>
  );
}
